/**
 * 统一键盘导航 Hook
 * 处理 Tab/Shift+Tab、方向键、回车键等键盘操作
 */

import { useEffect, useCallback, useRef } from 'react';
import { UnifiedPosition, CurrentStateAndPosition } from './useUnifiedNavigationState';
import { useNavigationMap, getNextTabPosition, getNextScenesTablePosition, getNextElementTablePosition, buildElementLayoutMap, buildElementColumnsLayout } from './useNavigationMap';
import { useUnifiedFieldEdit } from './useUnifiedFieldEdit';
import { SceneWithUserNames } from '../../../types/scene.types';
import { ElementWithUserNames } from '../../../types/element.types';

interface UseUnifiedKeyboardNavigationOptions {
  enabled?: boolean;
  scenes: SceneWithUserNames[];
  selectedSceneId: string | null;
  elements: ElementWithUserNames[];
  columns: Array<{ key: string }>;
  currentStateAndPosition: CurrentStateAndPosition | null;
  setSelection: (position: UnifiedPosition | null) => void;
  setEditing: (position: UnifiedPosition | null) => void;
  setSelectedSceneId: (sceneId: string | null) => void;
  scenesTableElementRefs: Map<string, HTMLElement>;
  sceneDetailsElementRefs: Map<string, HTMLElement>;
  elementTableElementRefs: Map<string, HTMLElement>;
  getPositionFromElement: (element: HTMLElement) => UnifiedPosition | null;
}

/**
 * 从 CurrentStateAndPosition 提取 UnifiedPosition
 */
function extractPosition(stateAndPosition: CurrentStateAndPosition): UnifiedPosition | null {
  if (!stateAndPosition) return null;
  const { state, ...position } = stateAndPosition;
  return position as UnifiedPosition;
}

/**
 * 根据位置获取 DOM 元素
 * 
 * 查找策略：
 * 1. 优先从 refs 中查找（性能更好）
 * 2. 如果 refs 中没有，从 DOM 中查找（使用 data-* 属性，处理元素刚渲染的情况）
 */
function getElementByPosition(
  position: UnifiedPosition,
  refs: {
    scenesTableElementRefs: Map<string, HTMLElement>;
    sceneDetailsElementRefs: Map<string, HTMLElement>;
    elementTableElementRefs: Map<string, HTMLElement>;
  }
): HTMLElement | null {
  switch (position.type) {
    case 'scenesTable': {
      const key = `${position.row}-${position.col}`;
      // 优先从 refs 中查找
      let element = refs.scenesTableElementRefs.get(key);
      if (element) {
        // 如果 refs 中的元素是 td，尝试查找其内部的输入框
        if (element.tagName === 'TD') {
          const input = element.querySelector('input, button.excel-cell-button') as HTMLElement | null;
          if (input) return input;
        }
        return element;
      }
      
      // 如果 refs 中没有，从 DOM 中查找（使用 data-* 属性）
      // 查找输入框：input[data-row-id="${sceneID}"][data-col-key="${colKey}"]
      // 查找下拉按钮：div.excel-cell-button[data-scene-id="${sceneID}"][data-field="${colKey}"]
      // 查找下拉字段 wrapper：div.breakdown-editable-cell-wrapper[data-column-key="${colKey}"]
      if (position.sceneID && position.colKey) {
        // 先查找输入框（使用 data-row-id 和 data-col-key）
        const foundInput = document.querySelector(
          `input[data-row-id="${position.sceneID}"][data-col-key="${position.colKey}"]`
        ) as HTMLElement | null;
        if (foundInput) return foundInput;
        
        // 查找下拉按钮（使用 data-scene-id 和 data-field，用于 sceneInteriorExterior 和 sceneDayNight）
        // 注意：表格模式下，下拉按钮可能不渲染，需要查找 wrapper 或单元格
        const foundButton = document.querySelector(
          `div.excel-cell-button[data-scene-id="${position.sceneID}"][data-field="${position.colKey}"]`
        ) as HTMLElement | null;
        if (foundButton) return foundButton;
        
        // 查找下拉字段 wrapper（表格模式下，下拉字段使用 wrapper div）
        const foundWrapper = document.querySelector(
          `.breakdown-editable-cell-wrapper[data-column-key="${position.colKey}"]`
        ) as HTMLElement | null;
        if (foundWrapper) {
          // 检查是否是当前行的 wrapper（通过查找最近的 td）
          const td = foundWrapper.closest('td[data-row-id]');
          if (td && td.getAttribute('data-row-id') === position.sceneID) {
            return foundWrapper;
          }
        }
        
        // 如果找不到可聚焦元素，查找单元格，然后在单元格内查找输入框
        const td = document.querySelector(
          `td[data-row-id="${position.sceneID}"][data-col-key="${position.colKey}"]`
        ) as HTMLElement | null;
        if (td) {
          // 在单元格内查找输入框或按钮（优先查找可聚焦元素）
          const input = td.querySelector('input, button.excel-cell-button') as HTMLElement | null;
          if (input) return input;
          // 如果找不到可聚焦元素，检查 td 是否有 tabIndex（选中状态时 td 有 tabIndex={0}）
          // 如果有 tabIndex，返回 td 本身（用于 Tab 导航时跳转到下一个位置）
          if (td.tabIndex !== undefined && td.tabIndex >= 0) {
            return td;
          }
          // 如果没有 tabIndex，返回 null（让调用者知道需要等待渲染输入框）
          return null;
        }
      }
      return null;
    }
    case 'sceneDetails': {
      // 优先从 refs 中查找
      let element = refs.sceneDetailsElementRefs.get(position.field);
      if (element) return element;
      
      // 如果 refs 中没有，从 DOM 中查找
      if (position.sceneID && position.field) {
        // 查找 input[data-field="${field}"] 或 button[data-field="${field}"] 或 div[data-field="${field}"]
        const foundElement = document.querySelector(
          `input[data-field="${position.field}"], 
           button[data-field="${position.field}"],
           div[data-field="${position.field}"]`
        ) as HTMLElement | null;
        if (foundElement) return foundElement;
      }
      return null;
    }
    case 'elementTable': {
      const inputKey = `${position.elementID}-${position.field}`;
      // 优先从 refs 中查找
      const element = refs.elementTableElementRefs.get(inputKey);
      if (element) return element;
      
      // 如果 refs 中没有，从 DOM 中查找
      if (position.elementID && position.field) {
        const foundInput = document.querySelector(
          `input[data-item-id="${position.elementID}"][data-field="${position.field}"]`
        ) as HTMLElement | null;
        if (foundInput) return foundInput;
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * 聚焦元素的辅助函数
 */
function focusElement(element: HTMLElement, clickX?: number): boolean {
  try {
    // 检查是否有 onEnterEditing 回调（通过元素属性）
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      const onEnterEditing = (element as any).__onEnterEditing;
      if (onEnterEditing && typeof onEnterEditing === 'function') {
        // 先调用 onEnterEditing 回调（用于初始化本地输入值等）
        onEnterEditing(element);
      }
    }

    // 聚焦元素
    element.focus();
    
    // 验证聚焦是否成功
    const focused = document.activeElement === element;

    // 如果是输入框或文本域，处理文本选择或光标定位
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      const input = element as HTMLInputElement | HTMLTextAreaElement;
      const inputType = element.tagName === 'INPUT' ? (element as HTMLInputElement).type : 'textarea';
      
      // 如果提供了 clickX（双击场景），根据坐标定位光标
      if (clickX !== undefined && inputType !== 'number') {
        const textLength = input.value.length;
        const cursorPosition = Math.min(Math.max(0, Math.round((clickX / input.offsetWidth) * textLength)), textLength);
        setTimeout(() => {
          if (input === document.activeElement) {
            input.setSelectionRange(cursorPosition, cursorPosition);
          }
        }, 0);
      }
      // 否则（Tab 导航场景），全选文本
      else if (inputType !== 'number' && inputType !== 'date' && inputType !== 'time' && inputType !== 'datetime-local') {
        // 对于文本输入框，全选文本内容
        setTimeout(() => {
          if (input === document.activeElement && input.value.length > 0) {
            input.select();
          }
        }, 0);
      }
    }
    
    return focused;
  } catch (error) {
    console.error(`[Tab导航-聚焦] 聚焦失败:`, error);
    return false;
  }
}

/**
 * 关闭所有打开的下拉菜单
 * 通过触发下拉按钮的 blur 事件来关闭菜单
 */
function closeAllOpenDropdownMenus() {
  // 查找所有下拉按钮（sceneDetails 的内外、日夜，包括表格和详情页）
  const dropdownButtons = document.querySelectorAll(
    '.scene-detail-select-button[data-field="sceneInteriorExterior"], ' +
    '.scene-detail-select-button[data-field="sceneDayNight"], ' +
    'button.excel-cell-button[data-field="sceneInteriorExterior"], ' +
    'button.excel-cell-button[data-field="sceneDayNight"]'
  ) as NodeListOf<HTMLElement>;
  
  // 对每个下拉按钮，如果它有 editing 类（表示菜单打开），关闭菜单
  dropdownButtons.forEach((button) => {
    if (button.classList.contains('editing')) {
      // 设置标记，表示这是 Tab 导航导致的 blur
      // 这样 handleBlur 会知道这是 Tab 导航，不会阻止关闭菜单
      button.setAttribute('data-tab-navigating', 'true');
      
      // 触发 blur 事件，这会调用 handleBlur，从而关闭菜单
      // 注意：即使焦点转移到另一个下拉按钮，handleBlur 也会被调用
      // 但由于我们设置了 data-tab-navigating 标记，handleBlur 会正确处理
      button.blur();
      
      // 清除标记（延迟清除，确保 blur 事件处理完成）
      setTimeout(() => {
        button.removeAttribute('data-tab-navigating');
      }, 10);
    }
  });
}

/**
 * 聚焦并选中位置（用于方向键导航）
 * 
 * 处理逻辑：
 * 1. 先尝试获取元素
 * 2. 如果元素不存在，先设置选中状态（触发渲染），等待渲染完成后再获取并聚焦
 * 3. 如果元素存在，直接设置选中状态并聚焦
 */
function focusAndSelectPosition(
  position: UnifiedPosition,
  refs: {
    scenesTableElementRefs: Map<string, HTMLElement>;
    sceneDetailsElementRefs: Map<string, HTMLElement>;
    elementTableElementRefs: Map<string, HTMLElement>;
  },
  setSelection: (position: UnifiedPosition | null) => void
) {
  // 在聚焦新元素之前，先失焦所有 scenesTable 的单元格（防止浏览器默认聚焦边框）
  refs.scenesTableElementRefs.forEach((element) => {
    if (element.tagName === 'TD' && element === document.activeElement) {
      (element as HTMLElement).blur();
    }
  });
  
  // 先尝试获取元素
  let element = getElementByPosition(position, refs);
  
  // 检查元素是否是 td（选中状态的单元格）
  const isTableCell = element && element.tagName === 'TD';
  
  // 如果元素不存在，或者是 td（选中状态），先设置选中状态，等待渲染
  if (!element || isTableCell) {
    // 先设置选中状态（触发渲染）
    setSelection(position);
    
    // 等待渲染完成（使用双重 requestAnimationFrame + setTimeout）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          // 再次尝试获取元素（应该能找到 td 元素）
          element = getElementByPosition(position, refs);
          
          if (element) {
            // 聚焦元素（td 元素在选中状态时有 tabIndex={0}）
            element.focus();
          }
        }, 0);
      });
    });
    return;
  }
  
  // 如果元素存在，直接设置选中状态并聚焦
  setSelection(position);
  element.focus();
}

/**
 * 聚焦并激活位置
 * 
 * 处理逻辑：
 * 1. 先尝试获取元素
 * 2. 如果元素不存在，先设置状态（触发渲染），等待渲染完成后再获取并聚焦
 * 3. 如果元素存在，直接设置状态并聚焦
 */
function focusAndActivatePosition(
  position: UnifiedPosition,
  refs: {
    scenesTableElementRefs: Map<string, HTMLElement>;
    sceneDetailsElementRefs: Map<string, HTMLElement>;
    elementTableElementRefs: Map<string, HTMLElement>;
  },
  setEditing: (position: UnifiedPosition | null) => void,
  clickX?: number,
  activateFieldEdit?: (options: { element: HTMLElement; autoFocus: boolean; selectText: boolean }) => void
) {
  // 在聚焦新元素之前，先关闭所有打开的下拉菜单
  closeAllOpenDropdownMenus();
  
  // 在聚焦新元素之前，先失焦所有 scenesTable 的单元格（防止浏览器默认聚焦边框）
  refs.scenesTableElementRefs.forEach((element) => {
    if (element.tagName === 'TD' && element === document.activeElement) {
      (element as HTMLElement).blur();
    }
  });
  
  // 先尝试获取元素
  let element = getElementByPosition(position, refs);
  
  // 检查元素是否是 td（选中状态的单元格）
  const isTableCell = element && element.tagName === 'TD';
  
  // 检查元素是否是可聚焦的输入框/按钮等（不包括 td）
  const isFocusableInput = element && (
    element.tagName === 'INPUT' ||
    element.tagName === 'BUTTON' ||
    (element.tagName === 'DIV' && !isTableCell) ||
    element.tagName === 'TEXTAREA'
  );
  
  // 如果元素不存在，或者是 td（选中状态），先设置编辑状态，等待输入框渲染
  if (!element || isTableCell) {
    // 先设置编辑状态（触发输入框渲染）
    setEditing(position);
    
    // 等待渲染完成（使用双重 requestAnimationFrame + setTimeout，参考 activateFieldEdit 的实现）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          // 再次尝试获取元素（应该能找到输入框）
          element = getElementByPosition(position, refs);
          
          if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'BUTTON' || element.tagName === 'DIV')) {
            // 聚焦输入框或其他可聚焦元素
            // 如果是输入框且配置了 selectText，使用 activateFieldEdit（回车键场景）
            if ((element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') && activateFieldEdit) {
              const selectText = (element as any).__selectText === true;
              const onEnterEditing = (element as any).__onEnterEditing;
              
              if (selectText) {
                // 先调用 onEnterEditing 回调（如果有）
                if (onEnterEditing && typeof onEnterEditing === 'function') {
                  onEnterEditing(element);
                }
                // 使用 activateFieldEdit 激活编辑并全选文本
                activateFieldEdit({
                  element,
                  autoFocus: true,
                  selectText: true,
                });
              } else {
                // 否则使用 focusElement（Tab 导航场景）
                focusElement(element, clickX);
              }
            } else {
              // 非输入框或没有 activateFieldEdit，使用 focusElement
              focusElement(element, clickX);
            }
            
            // 如果是下拉按钮，打开下拉菜单
            if (element.classList.contains('scene-detail-select-button') || element.classList.contains('excel-cell-button')) {
              const openMenu = (element as any).__openMenu;
              if (openMenu && typeof openMenu === 'function') {
                openMenu(element);
              }
            }
          }
        }, 0);
      });
    });
    return;
  }
  
  // 如果元素存在且是可聚焦的输入框/按钮等，直接设置状态并聚焦
  if (isFocusableInput) {
    // 设置编辑状态
    setEditing(position);
    
    // 聚焦元素
    // 如果是输入框且配置了 selectText，使用 activateFieldEdit（回车键场景）
    if ((element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') && activateFieldEdit) {
      const selectText = (element as any).__selectText === true;
      const onEnterEditing = (element as any).__onEnterEditing;
      
      if (selectText) {
        // 先调用 onEnterEditing 回调（如果有）
        if (onEnterEditing && typeof onEnterEditing === 'function') {
          onEnterEditing(element);
        }
        // 使用 activateFieldEdit 激活编辑并全选文本
        activateFieldEdit({
          element,
          autoFocus: true,
          selectText: true,
        });
      } else {
        // 否则使用 focusElement（Tab 导航场景）
        focusElement(element, clickX);
      }
    } else {
      // 非输入框或没有 activateFieldEdit，使用 focusElement
      focusElement(element, clickX);
    }
    
    // 如果是下拉按钮，打开下拉菜单
    if (element.classList.contains('scene-detail-select-button') || element.classList.contains('excel-cell-button')) {
      const openMenu = (element as any).__openMenu;
      if (openMenu && typeof openMenu === 'function') {
        openMenu(element);
      }
    }
  } else {
    // 其他情况，先设置状态，等待渲染
    setEditing(position);
  }
}

export function useUnifiedKeyboardNavigation(options: UseUnifiedKeyboardNavigationOptions) {
  const {
    enabled = true,
    scenes,
    selectedSceneId,
    elements,
    columns,
    currentStateAndPosition,
    setSelection,
    setEditing,
    setSelectedSceneId,
    scenesTableElementRefs,
    sceneDetailsElementRefs,
    elementTableElementRefs,
    getPositionFromElement,
  } = options;

  const { activateFieldEdit } = useUnifiedFieldEdit();

  // 使用 useRef 存储最新的 currentStateAndPosition，避免闭包问题
  const currentStateAndPositionRef = useRef<CurrentStateAndPosition | null>(currentStateAndPosition);
  
  // 同步 currentStateAndPosition 到 ref
  useEffect(() => {
    currentStateAndPositionRef.current = currentStateAndPosition;
  }, [currentStateAndPosition]);

  const { tabNavigationMap, tabNavigationMapFromPeriod } = useNavigationMap(
    scenes,
    selectedSceneId,
    elements,
    columns
  );

  // 处理 Tab 键导航
  const handleTabKey = useCallback((e: KeyboardEvent) => {
    // 查找替换弹窗打开时，不接管 Tab；交给弹窗内部的焦点循环处理
    const findReplaceDialogOpen = !!document.querySelector('.find-replace-container');
    if (findReplaceDialogOpen) {
      return;
    }

    // 使用 ref 获取最新的状态，避免闭包问题
    const latestState = currentStateAndPositionRef.current;
    
    // 优先检查是否有统一状态（无论是 selected 还是 editing）
    if (latestState) {
      const currentPos = extractPosition(latestState);
      if (!currentPos) return;
      
      // 拦截 Tab 键（必须在捕获阶段立即拦截，防止浏览器默认行为）
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // 在 Tab 导航之前，先关闭所有打开的下拉菜单
      closeAllOpenDropdownMenus();
      
      // 获取当前活动元素
      const activeElement = document.activeElement;
      
      // 如果当前是选中状态，先失焦当前单元格（防止浏览器默认聚焦）
      if (latestState.state === 'selected') {
        // 获取当前选中的单元格元素
        const currentElement = getElementByPosition(currentPos, {
          scenesTableElementRefs,
          sceneDetailsElementRefs,
          elementTableElementRefs,
        });
        
        // 如果当前活动元素是当前选中的单元格（td），先失焦
        if (activeElement && currentElement && activeElement === currentElement) {
          (activeElement as HTMLElement).blur();
        }
        
        // 清除选中状态（这会移除 tabIndex，防止后续被聚焦）
        setSelection(null);
        
        // 立即失焦所有 scenesTable 的单元格（防止浏览器默认聚焦边框）
        // 使用同步方式，确保在浏览器默认行为之前执行
        scenesTableElementRefs.forEach((element) => {
          if (element.tagName === 'TD' && element === document.activeElement) {
            (element as HTMLElement).blur();
          }
        });
        
        // 使用 requestAnimationFrame 再次检查并失焦（防止浏览器延迟聚焦）
        requestAnimationFrame(() => {
          const currentActiveElement = document.activeElement;
          if (currentActiveElement && currentActiveElement.tagName === 'TD') {
            const td = currentActiveElement as HTMLTableCellElement;
            // 如果 td 有 tabIndex 且是 scenesTable 的单元格，失焦
            if (td.tabIndex !== undefined && td.tabIndex >= 0) {
              td.blur();
            }
          }
        });
      }
      
      // 如果当前是编辑状态且焦点在输入框上，先完成编辑（通过 blur 触发 onCellEditEnd）
      if (latestState.state === 'editing' && activeElement && activeElement.tagName === 'INPUT') {
        const input = activeElement as HTMLInputElement;
        const element = getElementByPosition(currentPos, {
          scenesTableElementRefs,
          sceneDetailsElementRefs,
          elementTableElementRefs,
        });
        // 如果当前编辑的元素匹配，先 blur 完成编辑
        if (element === activeElement) {
          input.blur();
        }
      }
      
      // 确定使用哪个导航地图
      // Shift+Tab（反向）时始终使用完整地图，不跳过字段
      // Tab（正向）时，如果从排期单元格出发，使用特殊地图（跳过中间字段）
      let map = tabNavigationMap;
      if (!e.shiftKey && currentPos.type === 'scenesTable' && currentPos.colKey === 'scenePeriod') {
        // 从排期单元格按 Tab（正向），使用特殊地图
        map = tabNavigationMapFromPeriod;
      }
      
      // 获取下一个位置
      const nextPosition = getNextTabPosition(currentPos, map, e.shiftKey);
      if (nextPosition) {
        // 延迟跳转，确保编辑完成后再跳转
        setTimeout(() => {
          // 跳转到下一个位置并进入编辑状态
          // focusAndActivatePosition 内部会处理失焦逻辑
          focusAndActivatePosition(nextPosition, {
            scenesTableElementRefs,
            sceneDetailsElementRefs,
            elementTableElementRefs,
          }, setEditing);
        }, 0);
      }
      return;
    }
    
    // 如果没有统一状态，尝试从 activeElement 获取位置
    const activeElement = document.activeElement;
    if (activeElement && activeElement instanceof HTMLElement) {
      const position = getPositionFromElement(activeElement);
      if (position) {
        // 从元素获取到位置，拦截 Tab 键并导航
        e.preventDefault();
        e.stopPropagation();
        
        // 在 Tab 导航之前，先关闭所有打开的下拉菜单
        closeAllOpenDropdownMenus();
        
        // 如果焦点在输入框上，先完成编辑
        if (activeElement.tagName === 'INPUT') {
          const input = activeElement as HTMLInputElement;
          input.blur();
        }
        
        // 确定使用哪个导航地图
        let map = tabNavigationMap;
        if (!e.shiftKey && position.type === 'scenesTable' && position.colKey === 'scenePeriod') {
          map = tabNavigationMapFromPeriod;
        }
        
        // 获取下一个位置
        const nextPosition = getNextTabPosition(position, map, e.shiftKey);
        if (nextPosition) {
          // 延迟跳转，确保编辑完成后再跳转
          setTimeout(() => {
            focusAndActivatePosition(nextPosition, {
              scenesTableElementRefs,
              sceneDetailsElementRefs,
              elementTableElementRefs,
            }, setEditing);
          }, 0);
        }
        return;
      }
    }
    
    // 如果没有统一状态且无法从元素获取位置，让浏览器默认处理
  }, [
    tabNavigationMap,
    tabNavigationMapFromPeriod,
    scenesTableElementRefs,
    sceneDetailsElementRefs,
    elementTableElementRefs,
    setSelection,
    setEditing,
    getPositionFromElement,
  ]);

  // 处理方向键导航
  const handleArrowKey = useCallback((e: KeyboardEvent) => {
    // 使用 ref 获取最新的状态，避免闭包问题
    const latestState = currentStateAndPositionRef.current;
    
    // 只在选中状态时处理方向键，编辑状态时不处理
    if (!latestState || latestState.state !== 'selected') return;

    const currentPos = extractPosition(latestState);
    if (!currentPos) return;

    // 如果当前状态是 scenesTable，处理方向键导航
    if (currentPos.type === 'scenesTable') {
      // 场次表仍为「选中」态时，用户可能把焦点移到侧栏其它输入框（如元素搜索）；此时应保留方向键用于移动文本光标，不移动单元格选中
      const activeEl = document.activeElement as HTMLElement | null;
      const isTypingSurface =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable);
      if (isTypingSurface) {
        const navEl = getElementByPosition(currentPos, {
          scenesTableElementRefs,
          sceneDetailsElementRefs,
          elementTableElementRefs,
        });
        const cellRoot = navEl?.closest?.('td') ?? navEl;
        if (!cellRoot || !cellRoot.contains(activeEl)) {
          return;
        }
      }

      // 立即阻止默认行为和事件传播，防止页面滚动
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const direction = e.key === 'ArrowUp' ? 'up' :
        e.key === 'ArrowDown' ? 'down' :
        e.key === 'ArrowLeft' ? 'left' : 'right';

      // 排序后 currentPos.row 可能滞后；优先按 sceneID 回算当前真实行，避免上下键跳首尾
      const resolvedCurrentRow = scenes.findIndex((s) => s.sceneID === currentPos.sceneID);
      const currentRow = resolvedCurrentRow >= 0 ? resolvedCurrentRow : currentPos.row;

      const nextPos = getNextScenesTablePosition(
        currentRow,
        currentPos.col,
        scenes.length,
        columns.length,
        direction
      );

      if (nextPos) {
        // 从 scenes 获取新行的 sceneID
        const nextScene = scenes[nextPos.row];
        const nextSceneID = nextScene?.sceneID || currentPos.sceneID;
        
        const nextPosition: UnifiedPosition = {
          type: 'scenesTable',
          sceneID: nextSceneID,
          row: nextPos.row,
          col: nextPos.col,
          colKey: columns[nextPos.col]?.key,
        };
        
        // 如果导航到不同行，更新 selectedSceneId
        if (nextPos.row !== currentRow && nextSceneID !== selectedSceneId) {
          setSelectedSceneId(nextSceneID);
        }
        
        // 使用 focusAndSelectPosition 设置状态并聚焦（参考 Tab 导航的实现）
        focusAndSelectPosition(nextPosition, {
          scenesTableElementRefs,
          sceneDetailsElementRefs,
          elementTableElementRefs,
        }, setSelection);
      }
      return;
    }

    // 如果当前状态是 elementTable，检查 activeElement 是否在表格中
    if (currentPos.type === 'elementTable') {
      const activeElement = document.activeElement;
      if (!activeElement) return;

      const isInElementTable = activeElement.closest('.element-table');
      if (!isInElementTable) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const direction = e.key === 'ArrowUp' ? 'up' :
        e.key === 'ArrowDown' ? 'down' :
        e.key === 'ArrowLeft' ? 'left' : 'right';

      const currentItemIndex = elements.findIndex(item => item.elementID === currentPos.elementID);
      if (currentItemIndex !== -1) {
        // 构建列布局映射和完整列布局
        const layoutMap = buildElementLayoutMap(elements);
        const columnsLayout = buildElementColumnsLayout(elements);
        
        const nextPos = getNextElementTablePosition(
          elements,
          currentItemIndex,
          currentPos.field,
          direction,
          layoutMap,
          columnsLayout
        );

        // 如果返回 null，表示不需要导航（例如循环后还是自己）
        if (nextPos) {
          const nextItem = elements[nextPos.itemIndex];
          const nextPosition: UnifiedPosition = {
            type: 'elementTable',
            elementID: nextItem.elementID,
            field: nextPos.field,
          };
          setSelection(nextPosition);
          
          // 聚焦到目标输入框
          const inputKey = `${nextItem.elementID}-${nextPos.field}`;
          const element = elementTableElementRefs.get(inputKey);
          if (element) {
            element.focus();
          }
        }
      }
    }
  }, [
    scenes,
    elements,
    columns,
    selectedSceneId,
    scenesTableElementRefs,
    sceneDetailsElementRefs,
    elementTableElementRefs,
    setSelection,
    setSelectedSceneId,
  ]);

  // 处理回车键
  const handleEnterKey = useCallback((e: KeyboardEvent) => {
    // 查找替换弹窗打开时，不接管 Enter；交给弹窗内部按钮/输入框处理
    const activeElement = document.activeElement as HTMLElement | null;
    const isInFindReplace = !!activeElement?.closest('.find-replace-container');
    if (isInFindReplace) {
      return;
    }

    // ElementSearch 打开且焦点在其内部（如「新建」按钮）时，不接管 Enter；否则会在捕获阶段先于按钮处理，
    // 把 Enter 当成「进入场次表选中格的编辑」而误触发左侧场次表。
    const isInElementSearch = !!activeElement?.closest('.element-search');
    if (isInElementSearch) {
      return;
    }

    // 使用 ref 获取最新的状态，避免闭包问题
    const latestState = currentStateAndPositionRef.current;

    if (!activeElement) return;

    // 如果正在编辑输入框
    if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
      // 检查是否处于统一管理的编辑状态
      if (latestState?.state === 'editing') {
        const currentPos = extractPosition(latestState);
        if (!currentPos) return;
        
        // 检查当前编辑位置是否匹配
        const element = getElementByPosition(currentPos, {
          scenesTableElementRefs,
          sceneDetailsElementRefs,
          elementTableElementRefs,
        });
        
        // 如果是统一管理的字段，按回车退出编辑状态，进入选中状态
        if (element === activeElement) {
          e.preventDefault();
          e.stopPropagation();

          // 先触发 blur 事件（这会触发 onBlur，从而调用保存逻辑）
          if (activeElement instanceof HTMLElement) {
            activeElement.blur();
          }

          // 等待保存完成后再切换状态（使用双重 requestAnimationFrame，确保 blur 事件处理完成）
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setEditing(null);
              setSelection(currentPos);
            });
          });
        }
        // 如果不是统一管理的字段，让浏览器默认处理
        return;
      }
      // 如果不在编辑状态，让浏览器默认处理
      return;
    }

    // 如果当前有选中位置，切换到编辑状态
    if (latestState?.state === 'selected') {
      const currentPos = extractPosition(latestState);
      if (!currentPos) return;
      
      e.preventDefault();
      e.stopPropagation();

      // 使用 focusAndActivatePosition 来处理进入编辑状态（与 Tab/Shift+Tab 对齐）
      // 传递 activateFieldEdit，以便在回车键场景下全选文本
      focusAndActivatePosition(currentPos, {
        scenesTableElementRefs,
        sceneDetailsElementRefs,
        elementTableElementRefs,
      }, setEditing, undefined, activateFieldEdit);
      
      return;
    } else if (latestState?.state === 'editing') {
      // 如果正在编辑（非输入框元素，如下拉按钮），退出编辑状态，进入选中状态
      const currentPos = extractPosition(latestState);
      if (!currentPos) return;
      
      // 检查是否是下拉按钮
      const isDropdownButton = activeElement.classList.contains('scene-detail-select-button') || 
                               activeElement.classList.contains('excel-cell-button');
      
      if (isDropdownButton) {
        // 下拉按钮：检查菜单是否打开
        const isMenuOpen = activeElement.classList.contains('editing');
        
        if (isMenuOpen) {
          // 菜单打开：检查是否有预选选项，如果有则选择它
          // DropdownMenu 组件会处理回车键选择，这里只需要关闭菜单并退出编辑状态
          // 注意：DropdownMenu 的回车键处理会调用 onSelect，从而触发保存
          // 如果用户没有用方向键导航过，DropdownMenu 会关闭菜单
          // 这里我们让 DropdownMenu 自己处理，如果菜单关闭了，我们再退出编辑状态
          // 但为了确保行为一致，我们直接关闭菜单并退出编辑状态
          e.preventDefault();
          e.stopPropagation();
          
          // 关闭菜单（通过 blur 触发 handleBlur）
          if (activeElement instanceof HTMLElement) {
            activeElement.blur();
          }
          
          // 等待菜单关闭后再切换状态
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setEditing(null);
              setSelection(currentPos);
            });
          });
        } else {
          // 菜单未打开：直接退出编辑状态
          e.preventDefault();
          e.stopPropagation();
          
          setEditing(null);
          setSelection(currentPos);
          
          // 失焦
          if (activeElement instanceof HTMLElement) {
            activeElement.blur();
          }
        }
      } else {
        // 其他非输入框元素：直接退出编辑状态
        e.preventDefault();
        e.stopPropagation();

        setEditing(null);
        setSelection(currentPos);

        // 失焦
        if (activeElement instanceof HTMLElement) {
          activeElement.blur();
        }
      }
    }
  }, [
    scenesTableElementRefs,
    sceneDetailsElementRefs,
    elementTableElementRefs,
    setSelection,
    setEditing,
    activateFieldEdit,
  ]);

  // 监听自定义 Tab 导航事件（从 TableCell 触发）
  useEffect(() => {
    if (!enabled) return;
    const handleUnifiedTabNavigate = (e: Event) => {
      const customEvent = e as CustomEvent<{ 
        currentPosition: UnifiedPosition;
        shiftKey: boolean;
        source: string;
      }>;
      const { currentPosition, shiftKey } = customEvent.detail;
      
      // 确定使用哪个导航地图
      // Shift+Tab（反向）时始终使用完整地图，不跳过字段
      // Tab（正向）时，如果从排期单元格出发，使用特殊地图（跳过中间字段）
      let map = tabNavigationMap;
      if (!shiftKey && currentPosition.type === 'scenesTable' && currentPosition.colKey === 'scenePeriod') {
        // 从排期单元格按 Tab（正向），使用特殊地图
        map = tabNavigationMapFromPeriod;
      }

      // 获取下一个位置
      const nextPosition = getNextTabPosition(currentPosition, map, shiftKey);
      if (nextPosition) {
        // 跳转到下一个位置并进入编辑状态
        focusAndActivatePosition(nextPosition, {
          scenesTableElementRefs,
          sceneDetailsElementRefs,
          elementTableElementRefs,
        }, setEditing);
      }
    };

    document.addEventListener('unifiedTabNavigate', handleUnifiedTabNavigate as EventListener);
    return () => {
      document.removeEventListener('unifiedTabNavigate', handleUnifiedTabNavigate as EventListener);
    };
  }, [enabled, tabNavigationMap, tabNavigationMapFromPeriod, scenesTableElementRefs, sceneDetailsElementRefs, elementTableElementRefs, setEditing]);

  // 注册全局键盘事件监听器（使用捕获阶段，确保在子组件阻止事件前处理）
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        handleTabKey(e);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        handleArrowKey(e);
      } else if (e.key === 'Enter') {
        handleEnterKey(e);
      }
    };

    document.addEventListener('keydown', handleKeyDown as (e: Event) => void, true); // 使用捕获阶段
    return () => {
      document.removeEventListener('keydown', handleKeyDown as (e: Event) => void, true);
    };
  }, [enabled, handleTabKey, handleArrowKey, handleEnterKey]);
}
